import { AxiosError } from "axios";
import { FROM_URL_PARAM, LOGOUT_PATH } from "./constants";
import {
  ContractType,
  UserProfile,
  UserRoles,
  UserPermissions,
  UserAccessType,
  UserFeatureAccessType,
  UserObjectAccessType,
  WithUserPermissions,
  WithUserRoles,
  WithIsAdminFlag,
  SkyportalFeatureName,
} from "./types";

export const getAxiosErrorStatus = (error: AxiosError): number => {
  if (error && error.isAxiosError && typeof error.response?.status === "number") {
    return error.response?.status;
  }
  return -1;
};

export const isUnauthorizedError = (error: AxiosError): boolean => getAxiosErrorStatus(error) === 401;

export const isForbiddenError = (error: AxiosError): boolean => getAxiosErrorStatus(error) === 403;

export const getLogoutPath = (): string => LOGOUT_PATH;

export const getAuthWebUrlWithFromQueryParam = (authWebUrl: string): string =>
  `${authWebUrl}?${FROM_URL_PARAM}=${window.location.href}`;

export const redirectToAuth = (authWebUrl: string): void => {
  window.location.href = getAuthWebUrlWithFromQueryParam(authWebUrl);
};

const getRolesFromPermissions = (
  permissions: UserPermissions,
  contractName: string,
  contractType: ContractType
): string[] =>
  (permissions.entity?.filter((entity) => entity.name === contractName && entity.entityType === contractType)[0]
    ?.roles as string[]) || [];

const composeRolesAccessesToFeatures = (rolesDescription: UserRoles, rolesGiven: string[]): UserFeatureAccessType =>
  Object.values(
    rolesGiven.reduce((acc, role) => {
      acc[role] = rolesDescription[role];
      return acc;
    }, {} as UserRoles)
  )
    .map((item) => item.features)
    .reduce((acc, featureItem) => {
      (Object.keys(featureItem) as SkyportalFeatureName[]).forEach((featureName) => {
        if (acc[featureName]) {
          acc[featureName] = Array.from(new Set([...(acc[featureName] || []), ...(featureItem[featureName] || [])]));
        } else {
          acc[featureName] = featureItem[featureName];
        }
      });
      return acc;
    }, {});

export const hasContractAccessToFeature = (
  userProfile: WithUserRoles & WithUserPermissions & WithIsAdminFlag,
  contractName: string,
  contractType: ContractType,
  feature: SkyportalFeatureName,
  accessTypesNeeded: UserAccessType[]
): boolean => {
  if (userProfile.is_admin) return true;
  if (userProfile && userProfile.roles && userProfile.permissions) {
    const { roles: rolesDescription, permissions } = userProfile;

    const rolesGiven = getRolesFromPermissions(permissions, contractName, contractType);
    const allAccessTypesGiven = composeRolesAccessesToFeatures(rolesDescription, rolesGiven);

    return accessTypesNeeded.every((type) => (allAccessTypesGiven[feature] || []).includes(type));
  }

  return false;
};

const getAccessesFromRoles = (
  rolesDescription: UserRoles,
  type: "objectTypes" | "features"
): UserFeatureAccessType | UserObjectAccessType =>
  Object.values(rolesDescription)
    .map((item) => item[type])
    .reduce<UserFeatureAccessType>((acc, featureItem) => {
      (Object.keys(featureItem) as SkyportalFeatureName[]).forEach((featureName) => {
        if (acc[featureName]) {
          acc[featureName] = Array.from(new Set([...(acc[featureName] || []), ...(featureItem[featureName] || [])]));
        } else {
          acc[featureName] = featureItem[featureName];
        }
      });
      return acc;
    }, {});

export const hasUserAccessToFeature = (
  userProfile: WithUserRoles & WithIsAdminFlag,
  feature: SkyportalFeatureName,
  accessTypesNeeded: UserAccessType[],
  excludeAdmins?: boolean
): boolean => {
  if (userProfile.is_admin && !excludeAdmins) return true;
  if (userProfile && userProfile.roles) {
    const { roles: rolesDescription } = userProfile;

    const allAccessTypesGiven = getAccessesFromRoles(rolesDescription, "features");

    return accessTypesNeeded.every((type) => (allAccessTypesGiven[feature] || []).includes(type));
  }

  return false;
};

export const hasUserAccessToObject = (
  userProfile: WithUserRoles & WithIsAdminFlag,
  object: string,
  accessTypesNeeded: UserAccessType[]
): boolean => {
  if (userProfile.is_admin) return true;
  if (userProfile && userProfile.roles) {
    const { roles: rolesDescription } = userProfile;

    const allAccessTypesGiven = getAccessesFromRoles(rolesDescription, "objectTypes") as UserObjectAccessType;

    return accessTypesNeeded.every((type) => (allAccessTypesGiven[object] || []).includes(type));
  }

  return false;
};

export const getUserProfileSkeleton = (): UserProfile => ({
  id: 1,
  name: "",
  email: "",
  is_admin: false,
  customerErpId: null,
  oktaId: null,
  settings: {
    tcAccepted: null,
  },
  capabilities: {
    pricing: { view: false },
    menuSettings: [],
    menuObjects: [],
  },
  contracts: {
    BU: [],
    BA: [],
    DR: [],
    IA: [],
    ST: [],
    NS: [],
    MW: [],
    SS: [],
    SE: [],
    SD: [],
  },
  roles: {},
  permissions: {},
  substitutingUser: null,
  exp: 0,
  iat: 0,
  iss: "",
  sub: "",
});
